import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Reveal from '../components/Reveal';
import useReveal from '../hooks/useReveal';
import usePageMeta from '../hooks/usePageMeta';
import './Home.css';

// Certification details — shown in a popover when a chip is clicked.
// Plain-language descriptions, not bureaucratic boilerplate, so a
// customer immediately understands what each badge actually means.
const CERTIFICATIONS = {
  HUB: {
    full: 'Historically Underutilized Business',
    body: 'A Texas state certification for businesses owned and operated by groups that have been historically underrepresented in state contracting. HUB certification gives Heaven Sent access to bid on State of Texas contracts and helps state agencies meet their HUB participation goals.',
  },
  MBE: {
    full: 'Minority Business Enterprise',
    body: 'A national certification recognizing businesses that are at least 51% owned, operated, and controlled by members of a minority group. MBE certification opens doors to corporate supplier-diversity programs and government contracting opportunities.',
  },
  WBE: {
    full: "Women's Business Enterprise",
    body: 'A national certification for businesses at least 51% owned, operated, and controlled by one or more women. WBE certification qualifies Heaven Sent for women-owned business contracting programs across both private and public sectors.',
  },
  SBE: {
    full: 'Small Business Enterprise',
    body: 'A certification recognizing small businesses that meet specific size and ownership criteria. SBE certification supports participation in government and corporate small-business contracting programs designed to keep purchasing dollars with local, independent companies.',
  },
};

const services = [
  { title: 'Custom Apparel', desc: 'T-shirts, polos, hoodies, and uniforms with your branding.', img: '/images/screen-print-tees.webp' },
  { title: 'Promotional Products', desc: 'Pens, mugs, bags, and giveaways that make an impression.', img: '/images/promo-pic2.webp' },
  { title: 'Laser Engraving', desc: 'Precision engraving on tumblers, awards, and more.', img: '/gallery/gallery-05-laser-engraving.webp' },
  { title: 'Banners & Signs', desc: 'Eye-catching banners, yard signs, and event displays.', img: '/gallery/gallery-07-IMG_6057.webp' },
  { title: 'Embroidery', desc: 'Professional embroidered logos on caps, shirts, and bags.', img: '/gallery/gallery-09-view-recent.webp' },
  { title: 'Corporate Gifts', desc: 'Curated gift sets and branded packages for any occasion.', img: '/images/promo-pic1.webp' },
];

// Trust badges — replaces the comma-separated meta line. Each chip
// gets a small SVG icon for visual weight and reads like a real
// brand-quality signal rather than footer fine print.
const TRUST_BADGES = [
  {
    label: 'Competitively Priced',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="8" r="4" /><path d="M4 21c1.5-4.5 5-7 8-7s6.5 2.5 8 7" />
      </svg>
    ),
  },
  {
    label: 'Free Quotes',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
      </svg>
    ),
  },
  {
    label: 'Quick Turnaround',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 22s7-7 7-12a7 7 0 1 0-14 0c0 5 7 12 7 12z" /><circle cx="12" cy="10" r="2.5" />
      </svg>
    ),
  },
];

// "How it works" steps. Three is the magic number: any more reads
// like a process flowchart, fewer feels incomplete.
const HOW_IT_WORKS = [
  {
    n: '01',
    title: 'Request a Quote',
    desc: 'Tell us what you need. Pick products, pick colors, upload your art. Takes about five minutes.',
  },
  {
    n: '02',
    title: 'Approve a Proof',
    desc: 'We send back a digital mockup with pricing. You confirm it looks right before anything gets printed.',
  },
  {
    n: '03',
    title: 'We Print & Ship',
    desc: 'Your order goes into production. Shipped to your door or ready for local pickup.',
  },
];

// Stats counter — count up from 0 to the target when the strip
// scrolls into view. Uses a single rAF loop driven off Date.now so
// the animation is frame-rate independent.
function StatsStrip() {
  const [ref, visible] = useReveal();
  return (
    <section ref={ref} className="stats-strip">
      <div className="stats-grid">
        <Stat label="Years in Business" target={20} suffix="+" visible={visible} />
        <Stat label="Orders Shipped"   target={20000} suffix="+" visible={visible} />
        <Stat label="Local Customers"  target={300} suffix="+" visible={visible} />
        <Stat label="Google Rating"    target={4.7} decimals={1} suffix="★" visible={visible} />
      </div>
    </section>
  );
}

function Stat({ label, target, suffix = '', decimals = 0, visible }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!visible) return;
    const start = performance.now();
    const dur = 1400;
    let raf = 0;
    function tick(now) {
      const t = Math.min(1, (now - start) / dur);
      // ease-out cubic — fast at the start, settles softly
      const eased = 1 - Math.pow(1 - t, 3);
      setN(target * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [visible, target]);
  const display = decimals > 0 ? n.toFixed(decimals) : Math.round(n).toLocaleString();
  return (
    <div className="stat">
      <div className="stat-value">{display}{suffix}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

function Home() {
  usePageMeta({
    title: 'Custom Apparel & Promotional Products in San Antonio',
    description: 'Heaven Sent Promotions crafts custom apparel, embroidery, screen printing, and promotional products in San Antonio. Woman & minority-owned. HUB, MBE, WBE, SBE certified. Free quotes, quick turnaround.',
    path: '/',
  });
  // Which certification chip is currently open in the popover, if any.
  // null = closed. Esc key + backdrop click both dismiss.
  const [openCert, setOpenCert] = useState(null);
  // Mobile viewport detection — the hero swaps the embroidery-machine
  // video out for a static banner image on phones. The 5MB MP4 decoded
  // on mobile chews battery and stutters on cheaper devices; a 73KB
  // JPG renders instantly. matchMedia keeps the swap live across
  // rotation / devtools resize.
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' &&
    window.matchMedia('(max-width: 768px)').matches
  );
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)');
    const onChange = (e) => setIsMobile(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  useEffect(() => {
    if (!openCert) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpenCert(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openCert]);

  return (
    <main>
      {/* === Hero === */}
      <section
        className="hero"
        /* Mobile only: the section becomes a full-bleed photo
           background with a darkening scrim layered on top so the
           white headline / CTAs stay readable. Inline style because
           CRA's css-loader can't resolve absolute /images URLs from
           CSS — same workaround the hero-flanks use. */
        style={isMobile ? {
          /* No scrim — the photo shows through as-is. Each text element
             gets its own dark text-shadow halo (in CSS below) so it
             reads white against any part of the image without dimming
             the photo itself. */
          background: 'url(/images/hero-mobile.jpg) center / cover no-repeat',
        } : undefined}>
        {/* Flat-lay flanks. The original hero-product photo is anchored
            to both edges of the section so the LEFT half of the flat
            lay (cotton, tumblers, gray tee) shows on the left edge and
            the RIGHT half (pink tee, keychains, pens) shows on the
            right, framing the central video. Each flank's inner edge
            fades into the hero gradient via a mask so they don't end
            in a hard line. Inline style for the URL keeps CRA's
            css-loader from trying to resolve the public-folder path. */}
        <div
          className="hero-flank hero-flank--left"
          style={{ backgroundImage: "url('/images/hero-product.webp')" }}
          aria-hidden="true"
        />
        <div
          className="hero-flank hero-flank--right"
          style={{ backgroundImage: "url('/images/hero-product.webp')" }}
          aria-hidden="true"
        />
        <div className="hero-stage">
          <div className="hero-glow" aria-hidden="true"></div>
          <div className="hero-vignette" aria-hidden="true"></div>

          <div className="hero-grid">
            <div className="hero-copy animate-in">
              <h1 className="hero-headline">
                <span className="hero-headline-script">Your vision,</span>
                <span className="hero-headline-display">our products</span>
              </h1>
              <p className="hero-sub">
                Custom apparel and promotional products, made local in San Antonio.
              </p>
              <div className="hero-buttons">
                <Link to="/quote" className="btn-pink btn-lg">Request a Quote</Link>
                <Link to="/gallery" className="btn-ghost btn-lg">View Our Work</Link>
              </div>

              {/* Trust strip sits under the CTAs — light brand fine
                  print reinforcing the hero's promise. */}
              <div className="trust-strip">
                {TRUST_BADGES.map((b, i) => (
                  <React.Fragment key={b.label}>
                    {i > 0 && <span className="trust-strip-dot">●</span>}
                    <span className="trust-strip-label">{b.label}</span>
                  </React.Fragment>
                ))}
              </div>

              {/* Certification row, styled to match the trust strip
                  above (uppercase tracked labels with rose-gold dot
                  separators) so the formal credentials read as
                  understated brand fine print rather than chunky
                  badges. Each acronym is still a button — clicking
                  opens the explainer popover. */}
              <div className="cert-inline" aria-label="Business certifications">
                <span className="cert-inline-label">Certified:</span>
                {['HUB', 'MBE', 'WBE', 'SBE'].map((c, i) => (
                  <React.Fragment key={c}>
                    {i > 0 && <span className="trust-strip-dot">●</span>}
                    <button
                      type="button"
                      className="cert-inline-link"
                      onClick={() => setOpenCert(c)}
                      aria-haspopup="dialog"
                      aria-label={`${c}. ${CERTIFICATIONS[c].full}. Click to learn more.`}
                    >
                      {c}
                    </button>
                  </React.Fragment>
                ))}
              </div>
            </div>

            <div className="hero-product animate-in animate-delay-2">
              {isMobile ? null : (
                <video
                  className="hero-product-img"
                  src="/images/hero-banner-video.mp4"
                  poster="/images/hero-product.webp"
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  aria-hidden="true"
                  ref={(el) => {
                    if (el) {
                      el.muted = true;
                      el.defaultMuted = true;
                      el.volume = 0;
                    }
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* === Stats counter strip === */}
      <StatsStrip />

      {/* === How it works === */}
      <section className="how-it-works">
        <Reveal>
          <p className="section-label">How It Works</p>
          <h2 className="section-title">From Idea to Doorstep in Three Steps</h2>
          <p className="section-subtitle">Custom apparel doesn't have to be confusing. Here's the whole process.</p>
        </Reveal>
        <div className="hiw-grid">
          {HOW_IT_WORKS.map((step, i) => (
            <Reveal key={step.n} delay={i * 120} className="hiw-card">
              <div className="hiw-num">{step.n}</div>
              <h3 className="hiw-title">{step.title}</h3>
              <p className="hiw-desc">{step.desc}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* === Services === */}
      <section className="services-section">
        <Reveal>
          <p className="section-label">What We Do</p>
          <h2 className="section-title">Our Services</h2>
          <p className="section-subtitle">From custom apparel to laser engraving, we bring your brand to life with quality products and expert craftsmanship.</p>
        </Reveal>
        <div className="services-grid">
          {services.map((s, i) => (
            <Reveal key={s.title} delay={(i % 3) * 100} className="service-card">
              <div className="service-img" style={{ backgroundImage: `url(${s.img})` }} />
              <div className="service-overlay">
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* === CTA === */}
      <section className="cta-section">
        <div className="cta-bg"></div>
        <Reveal className="cta-content">
          <p className="section-label">Free Estimates</p>
          <h2 className="script-font">Ready to Make Your Brand Stand Out?</h2>
          <p>From custom apparel to promotional products, we'll help you create something unforgettable. Get a free estimate today!</p>
          <div className="hero-buttons">
            <Link to="/quote" className="btn-pink">Request a Quote</Link>
            <Link to="/contact" className="btn-outline">Contact Us</Link>
          </div>
        </Reveal>
      </section>

      {/* === Certification popover ===
          Renders when a chip is clicked. Backdrop click + Escape both
          dismiss. The dialog itself stops click propagation so clicks
          inside don't bubble to the backdrop. */}
      {openCert && (
        <div
          className="cert-modal-overlay"
          onClick={() => setOpenCert(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="cert-modal-title"
        >
          <div className="cert-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="cert-modal-close"
              onClick={() => setOpenCert(null)}
              aria-label="Close"
            >
              ×
            </button>
            <div className="cert-modal-acronym">{openCert}</div>
            <h3 id="cert-modal-title" className="cert-modal-title">
              {CERTIFICATIONS[openCert].full}
            </h3>
            <p className="cert-modal-body">{CERTIFICATIONS[openCert].body}</p>
          </div>
        </div>
      )}
    </main>
  );
}

export default Home;
